import { request } from '@/utils/http'
import type { SimpleEnterpriseVO } from '@api/enterprise'

// ===================== 类型定义 =====================

// 分页请求基础接口
export interface PageRequest {
  pageNum: number
  pageSize: number
}

// 客户角色VO（返回对象）
export interface ClientRoleVO {
  id: string
  status: boolean
  code: string
  name: string
  type: number // 1-客户, 2-供应商
  description?: string
  dateCreated: string
  dateModified: string
}

// 分页角色响应
export interface PageClientRoleVO {
  list: ClientRoleVO[]
  total: number
  pageNum: number
  pageSize: number
  pages: number
}

// 角色创建请求
export interface ClientRoleCreateRequest {
  code: string
  name: string
  type: number // 1-客户, 2-供应商
  description?: string
}

// 角色更新请求
export interface ClientRoleUpdateRequest {
  id: string
  code?: string
  name?: string
  type?: number
  description?: string
}

// 角色状态更新请求
export interface ClientRoleStatusRequest {
  id: string
  status: boolean
}

// 角色删除请求
export interface ClientRoleDeleteRequest {
  id: string
}

// 角色分页查询请求
export interface ClientRolePageRequest extends PageRequest {
  status?: boolean
  keyword?: string // 关键字（角色代码或名称模糊查询）
}

// 权限配置请求
export interface ClientRolePrivilegeConfigRequest {
  roleId: string
  privilegeIds: string[]
}

// 客商配置请求
export interface ClientRoleEnterpriseConfigRequest {
  roleId: string
  enterpriseIds: string[]
}

// 权限查询请求
export interface ClientRolePrivilegeQueryRequest {
  roleId: string
}

// 客商查询请求
export interface ClientRoleEnterpriseQueryRequest {
  roleId: string
}

// 权限VO
export interface ClientPrivilegeVO {
  id: string
  name: string
  nameEn?: string
  parentId: string
  category?: string
  shape?: string
  point?: string
  description?: string
}

// 企业VO
export interface EnterpriseVO {
  id: string
  enterpriseName: string
  enterpriseNameEn?: string
  businessLicense?: string
  status: number
  enterpriseType: number
}

// 角色表单（前端使用，直接基于后端字段）
export interface RoleForm {
  code: string
  name: string
  type: number // 1-客户, 2-供应商
  description?: string
}

// ===================== 接口实现 =====================

/**
 * 获取角色列表
 */
export const getRoleList = (params: ClientRolePageRequest) => {
  return request.post<PageClientRoleVO>('/enterprise/tenant/clientRole/page', params)
}

/**
 * 创建角色
 */
export const createRole = (data: ClientRoleCreateRequest) => {
  return request.post<void>('/enterprise/tenant/clientRole/create', data)
}

/**
 * 更新角色
 */
export const updateRole = (data: ClientRoleUpdateRequest) => {
  return request.post<void>('/enterprise/tenant/clientRole/update', data)
}

/**
 * 删除角色
 */
export const deleteRole = (data: ClientRoleDeleteRequest) => {
  return request.post<void>('/enterprise/tenant/clientRole/delete', data)
}

/**
 * 更新角色状态
 */
export const updateRoleStatus = (data: ClientRoleStatusRequest) => {
  return request.post<void>('/enterprise/tenant/clientRole/status', data)
}

/**
 * 配置角色权限
 */
export const configRolePrivileges = (data: ClientRolePrivilegeConfigRequest) => {
  return request.post<void>('/enterprise/tenant/clientRole/privileges/config', data)
}

/**
 * 配置角色客商
 */
export const configRoleEnterprises = (data: ClientRoleEnterpriseConfigRequest) => {
  return request.post<void>('/enterprise/tenant/clientRole/enterprises/config', data)
}

/**
 * 查询角色权限配置
 */
export const getRolePrivileges = (params: ClientRolePrivilegeQueryRequest) => {
  return request.post<ClientPrivilegeVO[]>('/enterprise/tenant/clientRole/privileges/get', params)
}

/**
 * 查询角色客商配置
 */
export const getRoleEnterprises = (params: ClientRoleEnterpriseQueryRequest) => {
  return request.post<SimpleEnterpriseVO[]>('/enterprise/tenant/clientRole/enterprises/get', params)
}

/**
 * 查询全部菜单权限配置
 */
export const getAllPrivileges = () => {
  return request.post<ClientPrivilegeVO[]>('/enterprise/tenant/clientRole/privileges/all')
}

/**
 * 查询全部角色配置
 */
export const getAllRole = () => {
  return request.post<ClientRoleVO[]>('/enterprise/tenant/clientRole/all')
}